'use client'

import { type FC, useActionState, useEffect, useTransition } from 'react'

import { Button, Divider, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import { FORGOT_PASSWORD_INITIAL_STATE } from '@/lib/api/auth/auth.constants'
import { forgotPassword } from '@/lib/api/auth/auth.service'
import type {
  ForgotPasswordFormData,
  ForgotPasswordFormState,
} from '@/lib/api/auth/auth.types'
import { showSuccess } from '@/lib/notifications'

import CardFormAlert from '../components/form-error-message'
import LinkBackLogin from '../components/link-back-login'

const ForgotPasswordForm: FC = () => {
  const router = useRouter()
  const tAuth = useTranslations('auth')
  const tCommon = useTranslations('common')

  const schema = z.object({
    email: z
      .email({ error: tAuth('forgot-password.form.fields.email.error.invalid') })
      .min(1, { error: tAuth('forgot-password.form.fields.email.error.required') }),
  })

  const form = useForm<ForgotPasswordFormData>({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
    },
    validate: zod4Resolver(schema),
  })

  const [, startTransition] = useTransition()

  const [state, submitAction, isPending] = useActionState<
    ForgotPasswordFormState,
    ForgotPasswordFormData
  >(forgotPassword, FORGOT_PASSWORD_INITIAL_STATE)

  const handleSubmit = (values: ForgotPasswordFormData) => {
    startTransition(() => {
      submitAction(values)
    })
  }

  useEffect(() => {
    if (state.success && state.email) {
      showSuccess({
        color: 'green',
        title: tAuth('forgot-password.notifications.confirmation-sent.title'),
        message: tAuth('forgot-password.notifications.confirmation-sent.description', {
          email: state.email,
        }),
      })
      router.push(`/reset-password?userEmail=${state.email}`)
    }
  }, [state.success, state.email, router, tAuth])

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap='2xl'>
        <Stack gap='xl'>
          <Stack gap='sm' align='center'>
            <Title size='h2' fz={24} ta='center'>
              {tAuth('forgot-password.title')}
            </Title>

            <Text c='var(--mantine-color-text-dimmed)' size='xs' ta='center' w='80%'>
              {tAuth('forgot-password.description')}
            </Text>
          </Stack>

          <Stack gap='lg'>
            <TextInput
              label={tAuth('forgot-password.form.fields.email.label')}
              name='email'
              placeholder={tAuth('forgot-password.form.fields.email.placeholder')}
              required
              type='email'
              opacity={isPending ? 0.2 : 1}
              size='xs'
              style={{
                pointerEvents: isPending ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-out',
              }}
              {...form.getInputProps('email')}
            />

            <Button
              autoContrast
              loading={isPending}
              loaderProps={{ type: 'dots' }}
              type='submit'
              size='xs'
            >
              {tAuth('forgot-password.form.submit.label')}
            </Button>
          </Stack>
          <CardFormAlert message={state.errorMessage} />
        </Stack>
        <Divider
          color='var(--mantine-color-border-surface)'
          label={tCommon('or')}
          labelPosition='center'
        />
        <LinkBackLogin>{tAuth('forgot-password.back-button')}</LinkBackLogin>
      </Stack>
    </form>
  )
}

export default ForgotPasswordForm
